import { httpService } from '@/services/HttpService';
import type {
  PaginationMeta,
  SearchAppliedFilters,
  SearchFiltersResponse,
  SearchTrendingCategory,
} from '@/types';

export type SearchSuggestionApi = {
  type?: string;
  value?: string;
  url?: string;
};

export type SearchGalleryApi = Record<string, unknown>;

export type SearchGalleriesApiResponse = {
  data: SearchGalleryApi[];
  meta: PaginationMeta;
  filters?: SearchFiltersResponse;
};

export type SearchVerificationError = {
  requires_verification?: boolean;
  turnstile_site_key?: string;
  retry_after?: number;
  message?: string;
};

type SearchSuggestionsApiResponse = {
  data: SearchSuggestionApi[];
};

type SearchTrendingApiResponse = {
  data: SearchTrendingCategory[];
};

const toStringParams = (params: Record<string, string | number | undefined>): Record<string, string> => {
  const result: Record<string, string> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === '') {
      return;
    }

    result[key] = String(value);
  });

  return result;
};

export const searchApi = {
  fetchSuggestions: (searchQuery: string, turnstileToken?: string) => {
    const params = toStringParams({
      q: searchQuery.trim(),
      limit: 10,
      turnstile_token: turnstileToken,
    });
    const headers = turnstileToken ? { 'X-Turnstile-Token': turnstileToken } : undefined;

    return httpService.public.get<SearchSuggestionsApiResponse>('/public/search/autocomplete', params, headers);
  },

  searchGalleries: (
    searchQuery: string,
    filters: SearchAppliedFilters,
    page: number,
    perPage: number,
    turnstileToken?: string
  ) => {
    const params = toStringParams({
      q: searchQuery.trim(),
      category_id: filters.category_id,
      photographer_profile_id: filters.photographer_profile_id,
      model_profile_id: filters.model_profile_id,
      price_min: filters.price_min,
      price_max: filters.price_max,
      page,
      per_page: perPage,
      turnstile_token: turnstileToken,
    });

    const headers = turnstileToken ? { 'X-Turnstile-Token': turnstileToken } : undefined;

    return httpService.public.get<SearchGalleriesApiResponse>('/public/search/galleries', params, headers);
  },

  fetchAvailableFilters: () => {
    return httpService.public.get<SearchFiltersResponse>('/public/search/filters');
  },

  fetchTrending: (limit = 6) => {
    return httpService.public.get<SearchTrendingApiResponse>('/public/search/trending', toStringParams({ limit }));
  },

  fetchPopular: (limit = 6) => {
    return httpService.public.get<{ data: SearchGalleryApi[] }>('/public/search/popular', toStringParams({ limit }));
  },
};
